import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';

/**
 * Stock-themed parallax backdrop for the public hero.
 * Renders a real SPY candlestick chart (last ~80 trading days) that scrolls
 * horizontally on a loop — the most-recent candle is anchored near the
 * center of the viewport so it reads like a live TradingView chart.
 *
 * Ticker tape above pulls real last-2-day closes from `daily_prices` to
 * compute accurate % changes.
 */

const TAPE_TICKERS = ['AAPL','TSLA','NVDA','MSFT','META','AMZN','GOOGL','JPM','SPY','QQQ','AMD','NFLX'];
const CHART_TICKER = 'SPY';
const CHART_DAYS = 80;

type Candle = { o: number; h: number; l: number; c: number };
type Tape = { s: string; pct: number };

const StockParallaxBackdrop = () => {
  const [candles, setCandles] = useState<Candle[]>([]);
  const [tape, setTape] = useState<Tape[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // SPY candles
      const { data: spy } = await supabase
        .from('daily_prices')
        .select('open,high,low,close,date')
        .eq('ticker', CHART_TICKER)
        .order('date', { ascending: false })
        .limit(CHART_DAYS);
      if (!cancelled && spy && spy.length) {
        const rows = spy
          .slice()
          .reverse()
          .filter((r) => r.open != null && r.high != null && r.low != null && r.close != null)
          .map((r) => ({
            o: Number(r.open),
            h: Number(r.high),
            l: Number(r.low),
            c: Number(r.close),
          }));
        setCandles(rows);
      }
      // Tape — last 2 sessions per ticker
      const { data: tapeRows } = await supabase
        .from('daily_prices')
        .select('ticker,close,date')
        .in('ticker', TAPE_TICKERS)
        .order('date', { ascending: false })
        .limit(TAPE_TICKERS.length * 6);
      if (!cancelled && tapeRows) {
        const byTicker = new Map<string, { close: number; date: string }[]>();
        for (const r of tapeRows) {
          if (r.close == null) continue;
          const arr = byTicker.get(r.ticker) ?? [];
          arr.push({ close: Number(r.close), date: r.date as string });
          byTicker.set(r.ticker, arr);
        }
        const computed: Tape[] = TAPE_TICKERS
          .map((t) => {
            const rows = byTicker.get(t);
            if (!rows || rows.length < 2) return null;
            const pct = ((rows[0].close - rows[1].close) / rows[1].close) * 100;
            return { s: t, pct };
          })
          .filter((x): x is Tape => x !== null);
        setTape(computed);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build the SVG candle layout. Latest candle is anchored to the right edge
  // of one "copy" of the series — we then duplicate the series and translate
  // by -50% so the loop is seamless. CSS positions the strip so the seam
  // (= latest candle) sits at the horizontal center of the viewport.
  const chart = useMemo(() => {
    if (candles.length < 2) return null;
    const W_PER = 14;                     // px per candle (with gap)
    const stripW = candles.length * W_PER;
    const min = Math.min(...candles.map((c) => c.l));
    const max = Math.max(...candles.map((c) => c.h));
    const range = max - min || 1;
    const H = 200;
    const pad = 8;
    const yOf = (v: number) => H - pad - ((v - min) / range) * (H - pad * 2);
    const bars = candles.map((c, i) => {
      const x = i * W_PER + W_PER / 2;
      const up = c.c >= c.o;
      const color = up ? 'hsl(var(--positive))' : 'hsl(var(--negative))';
      const bodyTop = yOf(Math.max(c.o, c.c));
      const bodyBot = yOf(Math.min(c.o, c.c));
      return { x, up, color, bodyTop, bodyBot, wickTop: yOf(c.h), wickBot: yOf(c.l) };
    });
    return { bars, stripW, H };
  }, [candles]);

  // Scroll-driven depth: tied to this section's progress through the viewport.
  const containerRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });
  const gridY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 40]);
  const candleY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 80]);
  const candleOpacity = useTransform(scrollYProgress, [0, 1], [0.32, reduce ? 0.32 : 0.18]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {/* Grid */}
      <motion.div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--foreground)) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          animation: 'parallax-grid 60s linear infinite',
          y: gridY,
        }}
      />

      {/* Candle chart — scrolls leftward, latest candle pinned near center */}
      {chart && (
        <motion.div
          className="absolute left-1/2 top-1/2 -translate-y-1/2 h-40 md:h-56"
          style={{
            // Start so the seam (end of first copy = latest candle) is at viewport center.
            transform: `translate(-${chart.stripW}px, -50%)`,
            y: candleY,
            opacity: candleOpacity,
          }}
        >
          <div
            className="flex h-full items-center will-change-transform"
            style={{
              width: chart.stripW * 2,
              animation: 'parallax-candles 40s linear infinite',
            }}
          >
            {[0, 1].map((copy) => (
              <svg
                key={copy}
                width={chart.stripW}
                height="100%"
                viewBox={`0 0 ${chart.stripW} ${chart.H}`}
                preserveAspectRatio="none"
                style={{ flexShrink: 0 }}
              >
                {chart.bars.map((b, i) => (
                  <g key={i}>
                    <line
                      x1={b.x} x2={b.x}
                      y1={b.wickTop} y2={b.wickBot}
                      stroke={b.color}
                      strokeWidth={1}
                    />
                    <rect
                      x={b.x - 4}
                      y={b.bodyTop}
                      width={8}
                      height={Math.max(1, b.bodyBot - b.bodyTop)}
                      fill={b.color}
                    />
                  </g>
                ))}
              </svg>
            ))}
          </div>
        </motion.div>
      )}

      {/* Ticker tape */}
      <div className="absolute inset-x-0 top-4 z-0 hidden h-5 overflow-hidden opacity-50 md:block">
        <div
          className="flex h-full items-center gap-6 whitespace-nowrap font-mono text-[10px] tabular-nums will-change-transform"
          style={{ animation: 'parallax-fast 45s linear infinite', width: '200%' }}
        >
          {(tape.length ? [...tape, ...tape, ...tape] : []).map((t, idx) => {
            const up = t.pct >= 0;
            return (
              <span key={idx} className="inline-flex items-center gap-1.5">
                <span className="font-semibold text-foreground/70">{t.s}</span>
                <span className={up ? 'text-positive' : 'text-negative'}>
                  {up ? '+' : ''}{t.pct.toFixed(2)}%
                </span>
                <span className="text-foreground/20">·</span>
              </span>
            );
          })}
        </div>
      </div>

      {/* Fade for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/10 to-background/85" />
    </div>
  );
};

export default StockParallaxBackdrop;
